<script setup>
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  anchors: {
    type: Array,
    required: true,
  },
})
const { t } = useI18n()
const emit = defineEmits(['scrollToAnchor'])
const displayedTitle = computed(() =>
  window.innerWidth > 960 ? props.title : t('general.jumpTo')
)

function scrollToAnchor(slug) {
  emit('scrollToAnchor', slug)
}
</script>

<template>
  <nav class="modular-page-navigation">
    <div class="modular-page-navigation__title-container">
      <div class="modular-page-navigation__title">
        {{ displayedTitle }}
      </div>
    </div>

    <div class="modular-page-navigation__buttons">
      <button
        v-for="anchor in anchors"
        @click="scrollToAnchor(anchor.slug)"
        :key="anchor.slug"
        class="modular-page-navigation__button text__eyebrow-small"
      >
        {{ anchor.title }}
      </button>
    </div>
  </nav>
</template>

<style lang="scss">
@import '~/styles/utils/variables';
@import '~/styles/utils/functions';
@import '~/styles/vendors/sass-mq/mq';

.modular-page-navigation {
  background-color: #200929;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12);
  border-radius: 42px;
  overflow: hidden;
  display: flex;

  height: 56px;
  padding: 0 16px;
  margin: 0 auto;
  align-items: center;
  position: sticky;
  top: calc(100% - 87px);
  z-index: 90;

  @include mq($from: 'm') using ($from) {
    max-width: gs(5, 4, 'xxxs');
  }

  @include mq($from: 'l') using ($from) {
    top: calc(100% - 97px);
    padding: 0 40px;
    width: gs(22, 21, 'l');
    height: 65px;
  }

  &__title-container {
    flex-shrink: 0;
    margin-right: 0px;

    @include mq($from: 'l') using ($from) {
      margin-right: auto;
    }

    &::before {
      content: '';
      background: linear-gradient(
        90.25deg,
        #c80afb -11.69%,
        rgba(200, 10, 251, 0.47) 1.25%,
        rgba(200, 10, 251, 0) 42.88%
      );
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 2;
    }
  }

  &__title {
    font-size: 16px;
    letter-spacing: -0.01em;
    font-weight: $font-weight-3G;
    position: relative;
    z-index: 3;

    @include mq($from: 'l') using ($from) {
      font-size: 15px;
      color: $brand-white;
    }
  }

  &__buttons {
    margin-left: 25px;
    display: flex;
    align-items: center;
    height: 100%;
    overflow: auto;

    &::before {
      content: '';
      width: 25px;
      height: 100%;
      background: linear-gradient(to right, #200929 0%, rgba(#200929, 0) 100%);
      flex-shrink: 0;
      position: sticky;
      top: 0;
      left: 0;
      pointer-events: none;
    }
  }

  &__button {
    height: 100%;
    font-weight: $font-weight-4G;
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 0.27em;
    text-transform: uppercase;
    color: #82658e;
    transition: color 0.3s;
    padding: 0 0 0 25px;
    flex-shrink: 0;

    &:hover,
    &:focus,
    &:active {
      color: $brand-white;
    }

    &:not(:last-child)::after {
      content: '•';
      color: #82658e;
      margin: 0 0 0 25px;
    }
  }
}
</style>
